/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-04 20:28:08
 **/

import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/frameworks/designStuff/components";
import { useMinimizeChatList, usePhoneCall, useSidebarActions } from "@/frameworks/core/hooks";
import { colors } from "@/frameworks/core/styles";
import { Configure } from "@/frameworks/libraries/utilities";
import * as S from "../emotion";
// import { removeMeFromList } from "@/frameworks/core/utils";

interface ScreenShareVideoProps {
	callID: string;
	userID: string;
	src?: MediaProvider;
	isExpandIcon?: boolean;
	style?: CSSProperties;
	onClick?: () => void;
}

const LOGTAG = "#ScreenShareVideo#";

export default function ScreenShareVideo({ userID, callID, isExpandIcon, style, onClick }: ScreenShareVideoProps) {
	const [isHover, setIsHover] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const sidebarActions = useSidebarActions();
	const phoneCall = usePhoneCall();
	const callUsers = phoneCall.state.users(callID)[0];
	const remoteScreenStream = phoneCall.state.remote_screen_stream[0];
	const callerID = phoneCall.state.call(callID)?.[0]?.callerId;
	const { minimizeChatListActions } = useMinimizeChatList();

	const peerInfo = useMemo(() => callUsers?.[userID], [callUsers]);
	// const otherUserInfo = useMemo(() => (callUsers ? removeMeFromList(Object.keys(callUsers)) : []), [callUsers]);

	useEffect(() => {
		if (remoteScreenStream && videoRef.current) {
			videoRef.current.srcObject = remoteScreenStream;
			videoRef.current.onloadeddata = () => {
				const playPromise = videoRef.current?.play();
				if (playPromise) {
					playPromise
						.then(() => {
							console.debug(LOGTAG, "Remote screen media has been played");
						})
						.catch((error) => {
							console.debug(LOGTAG, "Remote screen media can't play", error);
						});
				}
			};
		}
	}, [remoteScreenStream]);

	return (
		<div
			style={{ position: "relative", ...style }}
			onMouseEnter={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
			onClick={onClick}
		>
			<S.Video
				id={`${userID} ${remoteScreenStream?.id ?? ""}`}
				ref={videoRef}
				width={style?.width}
				height={style?.height}
				autoPlay
				muted={userID === Configure.userID || peerInfo.media?.audio === "off"}
			/>
			{/* <S.LeftTopNameWrapper> //TODO 기획 확인 중
				<Typography typoSize="Body3_MD" typoColor="neutral_00_100">
					{_T("{{name}}의 화면공유", { name: callUsers[userID]?.name })}
				</Typography>
			</S.LeftTopNameWrapper> */}
			{isHover && isExpandIcon && (
				<S.RightBottomIconWrapper>
					<Button.Icon
						size="Xlarge"
						name="Fullscreen24"
						style={{
							background: colors.neutral_00_68,
							color: "black"
						}}
						onClick={() => {
							if (sidebarActions.isOpen("PhoneDetail")) {
								sidebarActions.hide("PhoneDetail");
							}
							minimizeChatListActions.appendAll();
							phoneCall.popup.show({
								callID: callID || "",
								callerID: callerID,
								uiType: "fullSizeShare",
								dominantUser: {
									userID,
									isScreen: true
								}
							});
						}}
					/>
				</S.RightBottomIconWrapper>
			)}
		</div>
	);
}
